import { Box, Container, Paper, Stack, Typography, useTheme } from "@mui/material";
import { PermissionListContextProvider, usePermissionListContext } from "./PermissionListContext";
import { useTranslation } from "react-i18next";
import { useResponsiveBreakpoints, useResponsivePadding } from "src/utils/useResponsiveBreakpoints";
import { useMemo, useState } from "react";
import { FoodsIcon, SearchBox } from "@foodpilot/foods";
import PermissionGrid from "src/pages/Settings/Permissions/PermissionGrid.tsx";
import { PermissionPopover } from "src/pages/Settings/Permissions/Popover/PermissionPopover.tsx";
import { PermissionModal } from "src/pages/Settings/Permissions/Popover/PermissionModal";
import { Permission } from "src/api/client-api/permissions/useGetAllPermissions";
import { useNavigateSettingsParent } from "../useNavigateSettingsParent";
import { useFoodpilotContext } from "src/context/FoodpilotContext";

type PermissionListComponentProps = {
    onCreatePermission: () => void;
};

const PermissionListComponent = (props: PermissionListComponentProps) => {
    const { onCreatePermission } = props;
    const theme = useTheme();
    const { t } = useTranslation();
    const { globalSearch } = usePermissionListContext();
    const { search, setSearch } = globalSearch;
    const { isAdmin } = useFoodpilotContext();

    const { isMobile, isDesktop } = useResponsiveBreakpoints();
    const pagePadding = useResponsivePadding();

    const PermissionsGridMemo = useMemo(() => {
        return <PermissionGrid />;
    }, []);

    return (
        <Box sx={{ padding: pagePadding }}>
            {!isMobile && (
                <Stack
                    direction="row"
                    useFlexGap
                    justifyContent={isDesktop ? "flex-start" : "space-between"}
                    alignItems="center"
                    sx={{
                        marginBottom: "40px",
                        ...(isDesktop && {
                            gap: "3rem",
                        }),
                    }}
                >
                    <Stack
                        direction="row"
                        useFlexGap
                        gap="12px"
                        alignItems="center"
                        sx={{
                            ...(isDesktop && {
                                marginLeft: "auto",
                                minWidth: "fit-content",
                                flexShrink: 0,
                            }),
                        }}
                    >
                        {isAdmin() && <PermissionPopover onClick={onCreatePermission} />}
                    </Stack>
                </Stack>
            )}
            {isMobile && (
                <Box
                    sx={{
                        marginBottom: "1.25rem",
                        marginTop: "4rem",
                        paddingLeft: 0,
                        paddingRight: 0,
                        width: "100%",
                        "& .MuiStack-root": {
                            minWidth: "unset !important",
                            width: "100%",
                        },
                    }}
                >
                    <SearchBox
                        variant="standalone"
                        value={search}
                        placeholder={t("rechercher_un_element")}
                        onChange={setSearch}
                    />
                </Box>
            )}

            <Box
                component="div"
                sx={{
                    borderRadius: "16px",
                    border: `1px solid ${theme.custom.grey[500]}`,
                    background: "#FFF",
                    marginTop: isMobile ? "0.25rem" : "0",
                }}
            >
                {PermissionsGridMemo}
            </Box>
        </Box>
    );
};

const PermissionList = () => {
    const navigateSettingsParent = useNavigateSettingsParent();
    const { t } = useTranslation();

    const [modalOpen, setModalOpen] = useState(false);
    const [editingPermission, setEditingPermission] = useState<Permission | null>(null);

    const handleGoBack = () => {
        navigateSettingsParent();
    };

    const handleCreatePermission = () => {
        setEditingPermission(null);
        setModalOpen(true);
    };

    const handleEditPermission = (permission: Permission) => {
        setEditingPermission(permission);
        setModalOpen(true);
    };

    const handleModalSuccess = () => {};

    return (
        <PermissionListContextProvider onEditPermission={handleEditPermission}>
            <Container
                component="main"
                maxWidth={false}
                sx={{
                    m: "0",
                    p: "5rem !important",
                }}
            >
                <Paper
                    elevation={0}
                    sx={{
                        width: "100%",
                        border: "1px solid #E7E6E4",
                        borderRadius: "16px",
                        overflow: "hidden",
                        backgroundColor: "white",
                        boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.08)",
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            padding: "32px",
                            borderBottom: "1px solid #E7E6E4",
                            backgroundColor: "white",
                        }}
                    >
                        <Box
                            sx={{
                                width: "28px",
                                height: "28px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: "pointer",
                            }}
                            onClick={handleGoBack}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    "& svg": {
                                        strokeWidth: "2px",
                                        stroke: "currentColor",
                                    },
                                }}
                            >
                                <FoodsIcon icon="arrowLeftShort" size={6} />
                            </Box>
                        </Box>
                        <Box sx={{ marginLeft: "16px" }}>
                            <Typography variant="number-inter" sx={{ lineHeight: "1.6em", color: "#393A36" }}>
                                {t("settings.permissions", "Permissions")}
                            </Typography>
                        </Box>
                    </Box>
                    <PermissionListComponent onCreatePermission={handleCreatePermission} />
                </Paper>
            </Container>

            {modalOpen && (
                <PermissionModal
                    key={editingPermission?.id ?? "create"}
                    open
                    onClose={() => setModalOpen(false)}
                    onSuccess={handleModalSuccess}
                    editingPermission={editingPermission}
                />
            )}
        </PermissionListContextProvider>
    );
};
export default PermissionList;
